import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { withAuthenticationRequired } from "@auth0/auth0-react";
import {
  Container,
  Card,
  Alert,
  Table,
  Button,
  Row as BootstrapRow,
  Col as BootstrapCol,
} from "react-bootstrap";
import { Form, Input, Select, Spin, message } from "antd";
import { FilterOutlined } from "@ant-design/icons";
import { useNavigate, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";

import PageTitle from "@/common/PageTitle/PageTitle";
import FormItem from "@/common/FormItem/FormItem";
import DateRangePicker from "@/common/DateRangePicker/DateRangePicker";
import PaginationRow from "@/components/PaginationRow/PaginationRow";
import {
  useCanViewPage,
  useCanPerformLifecycleDelete,
} from "@/contexts/SuperAdminIdentityContext";
import {
  useListTrackSongHistoryQuery,
  useCountTrackSongHistoryQuery,
  useCountTrackSongHistoryByStatusQuery,
  useCreateSongHistoryTombstonePurgeMutation,
  useLazyGetSongHistoryTombstonePurgeQuery,
  extractTracksErrorMessage,
  type ListTrackSongHistoryArgs,
  type TrackSongHistorySummary,
} from "@/features/tracks/tracksApi";
import { formatDateTime } from "@/features/processing/processingDisplay";
import { useUploaderDirectory } from "@/features/processing/uploaderDisplay";
import {
  TruncateCell,
  isFullTrackId,
  renderActivityBadge,
  renderOutcomeBadge,
  activityFilterOptionsForTab,
  outcomeFilterOptionsForTab,
} from "@/features/tracks/songHistoryDisplay";
import { formatEntityRef, normalizeRefSearchTerm } from "@/lib/entityRef";
import SongHistoryLifecycleTabs, {
  parseSongHistoryLifecycleView,
  type SongHistoryLifecycleView,
} from "@/pages/SongHistoryLifecycleTabs";
import SongHistoryTombstonePurgeModal from "@/pages/SongHistoryTombstonePurgeModal";
import styles from "./uploader-history.module.scss";

// Song History summary list — one row per Track; tabs follow Song Library lifecycle.
// View History → /uploader-history/:trackId.
// Deleted tab: Super Admin Tombstone Purge (Step 7) only — no restore.

const PAGE_ID = "uploader-history";
const PAGE_SIZE = 50;
const EMPTY_SONGS: TrackSongHistorySummary[] = [];
const TOMBSTONE_PURGE_TERMINAL = new Set([
  "completed",
  "completed_with_exceptions",
  "failed",
]);

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

const URL_KEYS = [
  "status",
  "qTitle",
  "qArtist",
  "qUploader",
  "firstUploadedFrom",
  "firstUploadedTo",
  "latestActivityFrom",
  "latestActivityTo",
  "latestActivityType",
  "currentOutcome",
  "trackId",
  "deletedFrom",
  "deletedTo",
  "deletedBy",
  "qDeletionReason",
] as const;

type UrlFilterKey = (typeof URL_KEYS)[number];

interface SongHistoryFilters {
  status?: SongHistoryLifecycleView;
  qTitle?: string;
  qArtist?: string;
  qUploader?: string;
  firstUploadedFrom?: string;
  firstUploadedTo?: string;
  latestActivityFrom?: string;
  latestActivityTo?: string;
  latestActivityType?: string;
  currentOutcome?: string;
  trackId?: string;
  deletedFrom?: string;
  deletedTo?: string;
  deletedBy?: string;
  qDeletionReason?: string;
}

function filtersFromSearchParams(params: URLSearchParams): SongHistoryFilters {
  const next: SongHistoryFilters = {
    status: parseSongHistoryLifecycleView(params.get("status")),
  };
  for (const key of URL_KEYS) {
    if (key === "status") continue;
    const value = params.get(key);
    if (value) next[key] = value;
  }
  return next;
}

function searchParamsFromFilters(filters: SongHistoryFilters): URLSearchParams {
  const params = new URLSearchParams();
  const status = filters.status || "active";
  params.set("status", status);
  for (const key of URL_KEYS) {
    if (key === "status") continue;
    const value = filters[key];
    if (value) params.set(key, value);
  }
  return params;
}

function minLenOrUndef(value: string, min = 2): string | undefined {
  const t = value.trim();
  return t.length >= min ? t : undefined;
}

const UploaderHistory: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const canView = useCanViewPage(PAGE_ID);
  const canLifecycle = useCanPerformLifecycleDelete();
  const uploaderDirectory = useUploaderDirectory();
  const [selectedTrackIds, setSelectedTrackIds] = useState<string[]>([]);
  const [selectAllMatching, setSelectAllMatching] = useState(false);
  const [bulkAction, setBulkAction] = useState<string>("");
  const [purgeModalOpen, setPurgeModalOpen] = useState(false);
  const [purgeBusy, setPurgeBusy] = useState(false);
  const [purgeError, setPurgeError] = useState<string | null>(null);
  const [createTombstonePurge] = useCreateSongHistoryTombstonePurgeMutation();
  const [fetchTombstonePurge] = useLazyGetSongHistoryTombstonePurgeQuery();

  const [filters, setFilters] = useState<SongHistoryFilters>(() =>
    filtersFromSearchParams(searchParams),
  );
  const [tokenStack, setTokenStack] = useState<(string | null)[]>([null]);
  const [resetCounter, setResetCounter] = useState(0);
  const [titleInput, setTitleInput] = useState(
    () => searchParams.get("qTitle") || "",
  );
  const [artistInput, setArtistInput] = useState(
    () => searchParams.get("qArtist") || "",
  );
  const [trackIdInput, setTrackIdInput] = useState(
    () => searchParams.get("trackId") || "",
  );
  const [uploaderInput, setUploaderInput] = useState("");
  const [deletedByInput, setDeletedByInput] = useState(
    () => searchParams.get("deletedBy") || "",
  );
  const [deletionReasonInput, setDeletionReasonInput] = useState(
    () => searchParams.get("qDeletionReason") || "",
  );
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  // setSearchParams can lag setFilters by a render; without this guard the
  // URL→filters effect briefly re-applies the previous tab and flashes rows.
  const skipUrlAdoptRef = useRef(false);

  const lifecycleStatus: SongHistoryLifecycleView =
    filters.status || "active";
  const currentToken = tokenStack[tokenStack.length - 1];

  const syncUrl = useCallback(
    (next: SongHistoryFilters) => {
      skipUrlAdoptRef.current = true;
      setSearchParams(searchParamsFromFilters(next), { replace: true });
    },
    [setSearchParams],
  );

  const resetPagination = useCallback(() => {
    setTokenStack([null]);
  }, []);

  const mergeFilters = (
    prev: SongHistoryFilters,
    patch: Partial<SongHistoryFilters>,
  ): SongHistoryFilters => {
    const next: SongHistoryFilters = { ...prev, ...patch };
    for (const key of Object.keys(patch) as UrlFilterKey[]) {
      if (patch[key] == null || patch[key] === "") {
        delete next[key];
      }
    }
    if (!next.status) next.status = "active";
    return next;
  };

  const applyFilters = useCallback(
    (patch: Partial<SongHistoryFilters>) => {
      const next = mergeFilters(filtersRef.current, patch);
      filtersRef.current = next;
      setFilters(next);
      syncUrl(next);
      resetPagination();
    },
    [resetPagination, syncUrl],
  );

  const handleLifecycleSelect = useCallback(
    (nextStatus: SongHistoryLifecycleView) => {
      const prev = filtersRef.current;
      const next: SongHistoryFilters = {
        ...prev,
        status: nextStatus,
      };
      // Drop Deleted-only filters when leaving Deleted.
      if (nextStatus !== "deleted") {
        delete next.deletedFrom;
        delete next.deletedTo;
        delete next.deletedBy;
        delete next.qDeletionReason;
        setDeletedByInput("");
        setDeletionReasonInput("");
      }
      // Drop outcome when leaving Active — tab already defines lifecycle state.
      if (nextStatus !== "active") {
        delete next.currentOutcome;
      } else if (next.currentOutcome) {
        const allowed = outcomeFilterOptionsForTab(nextStatus).map((o) => o.value);
        if (!allowed.includes(next.currentOutcome)) delete next.currentOutcome;
      }
      // Deleted drops Latest Activity type (Track ID takes that slot).
      if (nextStatus === "deleted") {
        delete next.latestActivityType;
      } else if (next.latestActivityType) {
        const allowed = activityFilterOptionsForTab(nextStatus).map((o) => o.value);
        if (!allowed.includes(next.latestActivityType)) {
          delete next.latestActivityType;
        }
      }
      filtersRef.current = next;
      setFilters(next);
      syncUrl(next);
      resetPagination();
      setResetCounter((n) => n + 1);
    },
    [resetPagination, syncUrl],
  );

  // Back/forward / shared URL: when the query string diverges from local
  // filter state, adopt the URL — but not while our own syncUrl/reset is
  // waiting for react-router to catch up (that caused Active→Archived flash).
  const urlKey = searchParams.toString();
  const filtersKey = searchParamsFromFilters(filters).toString();
  useEffect(() => {
    if (skipUrlAdoptRef.current) {
      if (urlKey === filtersKey) skipUrlAdoptRef.current = false;
      return;
    }
    if (urlKey === filtersKey) return;
    const fromUrl = filtersFromSearchParams(searchParams);
    filtersRef.current = fromUrl;
    setFilters(fromUrl);
    setTitleInput(fromUrl.qTitle || "");
    setArtistInput(fromUrl.qArtist || "");
    setTrackIdInput(fromUrl.trackId || "");
    setDeletedByInput(fromUrl.deletedBy || "");
    setDeletionReasonInput(fromUrl.qDeletionReason || "");
    setTokenStack([null]);
    setResetCounter((n) => n + 1);
  }, [urlKey, filtersKey, searchParams]);

  const handleReset = () => {
    const next: SongHistoryFilters = { status: lifecycleStatus };
    filtersRef.current = next;
    setFilters(next);
    setTitleInput("");
    setArtistInput("");
    setTrackIdInput("");
    setUploaderInput("");
    setDeletedByInput("");
    setDeletionReasonInput("");
    skipUrlAdoptRef.current = true;
    setSearchParams(searchParamsFromFilters(next), { replace: true });
    resetPagination();
    setResetCounter((n) => n + 1);
  };

  const listArgs = useMemo((): ListTrackSongHistoryArgs => {
    const args: ListTrackSongHistoryArgs = {
      limit: PAGE_SIZE,
      status: lifecycleStatus,
    };
    if (currentToken) args.pageToken = currentToken;
    if (filters.qTitle) args.qTitle = filters.qTitle;
    if (filters.qArtist) args.qArtist = filters.qArtist;
    if (filters.qUploader) args.qUploader = filters.qUploader;
    if (filters.firstUploadedFrom) args.firstUploadedFrom = filters.firstUploadedFrom;
    if (filters.firstUploadedTo) args.firstUploadedTo = filters.firstUploadedTo;
    if (filters.latestActivityFrom) args.latestActivityFrom = filters.latestActivityFrom;
    if (filters.latestActivityTo) args.latestActivityTo = filters.latestActivityTo;
    if (lifecycleStatus !== "deleted" && filters.latestActivityType) {
      args.latestActivityType = filters.latestActivityType;
    }
    if (lifecycleStatus === "active" && filters.currentOutcome) {
      args.currentOutcome = filters.currentOutcome;
    }
    if (filters.trackId) args.trackId = filters.trackId;
    if (lifecycleStatus === "deleted") {
      if (filters.deletedFrom) args.deletedFrom = filters.deletedFrom;
      if (filters.deletedTo) args.deletedTo = filters.deletedTo;
      if (filters.deletedBy) args.deletedBy = filters.deletedBy;
      if (filters.qDeletionReason) args.qDeletionReason = filters.qDeletionReason;
    }
    return args;
  }, [filters, currentToken, lifecycleStatus]);

  const countArgs = useMemo((): ListTrackSongHistoryArgs => {
    const { pageToken: _p, limit: _l, ...rest } = listArgs;
    return rest;
  }, [listArgs]);

  const {
    currentData,
    isLoading,
    isFetching,
    isError,
    error,
    fulfilledTimeStamp,
    refetch: refetchList,
  } = useListTrackSongHistoryQuery(listArgs, { skip: !canView });

  const countQuery = useCountTrackSongHistoryQuery(countArgs, {
    skip: !canView,
  });

  const tabCountsQuery = useCountTrackSongHistoryByStatusQuery(undefined, {
    skip: !canView,
  });

  // Clear rows before paint on tab change so Spin never overlays the prior tab.
  const [rowsSuppressed, setRowsSuppressed] = useState(false);
  const prevLifecycleRef = useRef(lifecycleStatus);
  const tabSwitchPending = prevLifecycleRef.current !== lifecycleStatus;
  useLayoutEffect(() => {
    if (prevLifecycleRef.current === lifecycleStatus) return;
    prevLifecycleRef.current = lifecycleStatus;
    setRowsSuppressed(true);
  }, [lifecycleStatus]);
  useLayoutEffect(() => {
    if (!rowsSuppressed) return;
    if (isLoading || isFetching) return;
    setRowsSuppressed(false);
  }, [rowsSuppressed, isLoading, isFetching, currentData, fulfilledTimeStamp]);

  // Tab switches must never paint the previous tab's rows.
  // Only currentData for the active args; stay empty while that request is in flight.
  const songs: TrackSongHistorySummary[] = useMemo(() => {
    if (tabSwitchPending || rowsSuppressed || isLoading || isFetching) {
      return EMPTY_SONGS;
    }
    const raw = currentData?.songs;
    if (!raw || raw.length === 0) return EMPTY_SONGS;
    return raw.filter((row) => row.status === lifecycleStatus);
  }, [
    currentData?.songs,
    isLoading,
    isFetching,
    lifecycleStatus,
    fulfilledTimeStamp,
    rowsSuppressed,
    tabSwitchPending,
  ]);

  const matchingTotal = useMemo(() => {
    if (isLoading || isFetching || !currentData) return null;
    if (countQuery.data && countQuery.data.count >= 0) {
      // Staging may ignore status= and return living totals; don't show a
      // inflated "of N" when this tab's filtered page is empty.
      if (songs.length === 0 && countQuery.data.count > 0) {
        return 0;
      }
      return countQuery.data.count;
    }
    return null;
  }, [countQuery.data, songs.length, isLoading, isFetching, currentData]);
  const matchingExact = countQuery.data?.exact === true;

  const clearSelection = useCallback(() => {
    setSelectedTrackIds([]);
    setSelectAllMatching(false);
    setBulkAction("");
  }, []);

  useEffect(() => {
    clearSelection();
  }, [lifecycleStatus, clearSelection]);

  const pageTrackIds = useMemo(
    () => songs.map((row) => row.trackId),
    [songs],
  );
  const allPageSelected =
    pageTrackIds.length > 0 &&
    pageTrackIds.every((id) => selectedTrackIds.includes(id));
  const somePageSelected =
    pageTrackIds.some((id) => selectedTrackIds.includes(id)) && !allPageSelected;

  const toggleSelectAllPage = () => {
    if (allPageSelected) {
      setSelectedTrackIds((prev) =>
        prev.filter((id) => !pageTrackIds.includes(id)),
      );
      setSelectAllMatching(false);
      return;
    }
    setSelectedTrackIds((prev) => {
      const next = new Set(prev);
      for (const id of pageTrackIds) next.add(id);
      return Array.from(next);
    });
    setSelectAllMatching(false);
  };

  const toggleRowSelected = (trackId: string) => {
    setSelectAllMatching(false);
    setSelectedTrackIds((prev) =>
      prev.includes(trackId)
        ? prev.filter((id) => id !== trackId)
        : [...prev, trackId],
    );
  };

  const showDeletedSelection = canLifecycle && lifecycleStatus === "deleted";
  const requestedPurgeCount = selectAllMatching
    ? matchingTotal ?? selectedTrackIds.length
    : selectedTrackIds.length;
  const offerSelectAllMatching =
    showDeletedSelection &&
    !selectAllMatching &&
    selectedTrackIds.length > 0 &&
    matchingTotal != null &&
    matchingTotal > selectedTrackIds.length;

  const handleBulkActionChange = (value: string) => {
    setBulkAction(value);
    if (value === "tombstonePurge") {
      if (requestedPurgeCount <= 0) {
        message.warning("Select at least one Deleted tombstone to purge");
        setBulkAction("");
        return;
      }
      setPurgeError(null);
      setPurgeModalOpen(true);
    }
  };

  const pollPurgeUntilDone = async (bulkOpId: string) => {
    for (let i = 0; i < 180; i += 1) {
      const op = await fetchTombstonePurge(bulkOpId).unwrap();
      if (TOMBSTONE_PURGE_TERMINAL.has(op.status)) return op;
      await sleep(1000);
    }
    throw new Error("Tombstone Purge is still running — refresh Deleted to verify");
  };

  const submitTombstonePurge = async (reason: string) => {
    if (!reason.trim()) {
      message.error("Purge reason is required");
      return;
    }
    setPurgeBusy(true);
    setPurgeError(null);
    try {
      const filter = {
        status: "deleted" as const,
        qTitle: filters.qTitle,
        qArtist: filters.qArtist,
        qUploader: filters.qUploader,
        firstUploadedFrom: filters.firstUploadedFrom,
        firstUploadedTo: filters.firstUploadedTo,
        latestActivityFrom: filters.latestActivityFrom,
        latestActivityTo: filters.latestActivityTo,
        latestActivityType: filters.latestActivityType,
        currentOutcome: filters.currentOutcome,
        trackId: filters.trackId,
        deletedFrom: filters.deletedFrom,
        deletedTo: filters.deletedTo,
        deletedBy: filters.deletedBy,
        qDeletionReason: filters.qDeletionReason,
      };
      const created = await createTombstonePurge({
        reason: reason.trim(),
        selectionMode: selectAllMatching ? "allMatching" : "ids",
        ids: selectAllMatching ? undefined : selectedTrackIds,
        filter,
        idempotencyKey: `sh-purge-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
      }).unwrap();

      const finalOp = await pollPurgeUntilDone(created.bulkOpId);
      const succeeded = finalOp.counters?.succeeded ?? 0;
      const failed = finalOp.counters?.failed ?? 0;
      if (finalOp.status === "failed") {
        throw new Error(finalOp.errorMessage || "Tombstone Purge failed");
      }
      if (failed > 0) {
        message.warning(
          `Tombstone Purge finished with exceptions: ${succeeded.toLocaleString()} purged, ${failed.toLocaleString()} failed`,
        );
      } else {
        message.success(
          `Purged ${succeeded.toLocaleString()} Song History tombstone${succeeded === 1 ? "" : "s"}`,
        );
      }
      setPurgeModalOpen(false);
      clearSelection();
      void refetchList();
      void tabCountsQuery.refetch();
      void countQuery.refetch();
    } catch (err) {
      const msg =
        extractTracksErrorMessage(err) ||
        (err instanceof Error ? err.message : "Tombstone Purge failed");
      setPurgeError(msg);
      message.error(msg);
    } finally {
      setPurgeBusy(false);
      setBulkAction("");
    }
  };

  const formatTabCount = (n: number | undefined) => {
    if (n == null || n < 0) return "";
    return ` (${n.toLocaleString()})`;
  };

  const activityOptions = activityFilterOptionsForTab(lifecycleStatus);
  const outcomeOptions = outcomeFilterOptionsForTab(lifecycleStatus);

  // Same typeable Select pattern as Song Processing Search: options from the
  // current page + live draft so free-text Enter still works.
  const buildTextOptions = (
    draft: string,
    applied: string | undefined,
    values: Array<string | null | undefined>,
  ) => {
    const seen = new Set<string>();
    const opts: { label: string; value: string }[] = [];
    const add = (raw?: string | null) => {
      const text = (raw || "").trim();
      if (!text) return;
      const key = text.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      opts.push({ label: text, value: text });
    };
    add(draft);
    add(applied);
    for (const value of values) add(value);
    return opts.slice(0, 40);
  };

  const titleOptions = useMemo(
    () =>
      buildTextOptions(
        titleInput,
        filters.qTitle,
        songs.map((s) => s.title),
      ),
    [titleInput, filters.qTitle, songs],
  );

  const artistOptions = useMemo(
    () =>
      buildTextOptions(
        artistInput,
        filters.qArtist,
        songs.map((s) => s.artistName),
      ),
    [artistInput, filters.qArtist, songs],
  );

  const deletedByOptions = useMemo(() => {
    const seen = new Set<string>();
    const opts: { label: string; value: string }[] = [];
    const add = (value: string, label: string) => {
      const key = value.toLowerCase();
      if (!value || seen.has(key)) return;
      seen.add(key);
      opts.push({ label, value });
    };
    // Prefer the employee directory (name label, Auth0 sub value) — same
    // contract as Original Uploader.
    for (const opt of uploaderDirectory.options) {
      add(opt.value, opt.label);
    }
    // Page rows whose actor isn't in directory: show resolved label when
    // possible, otherwise last-resort raw sub (never prefer raw over a name).
    for (const song of songs) {
      const sub = song.permanentlyDeletedBySub;
      if (!sub) continue;
      const resolved = uploaderDirectory.resolve(sub, null);
      add(sub, resolved.label);
    }
    if (filters.deletedBy) {
      const resolved = uploaderDirectory.resolve(filters.deletedBy, null);
      add(filters.deletedBy, resolved.label);
    }
    opts.sort((a, b) => a.label.localeCompare(b.label));
    return opts;
  }, [songs, filters.deletedBy, uploaderDirectory]);

  const deletionReasonOptions = useMemo(
    () =>
      buildTextOptions(
        deletionReasonInput,
        filters.qDeletionReason,
        songs.map((s) => s.permanentDeleteReason),
      ),
    [deletionReasonInput, filters.qDeletionReason, songs],
  );

  const handleDeletedByChange = (value: string | null | undefined) => {
    const next = value || undefined;
    setDeletedByInput(next || "");
    applyFilters({ deletedBy: next });
  };

  const handleDeletionReasonSearch = (value: string) => {
    const next = minLenOrUndef(value);
    setDeletionReasonInput(next ?? "");
    applyFilters({ qDeletionReason: next });
  };

  const trackIdOptions = useMemo(() => {
    const seen = new Set<string>();
    const opts: { label: string; value: string }[] = [];
    const add = (label: string, value: string) => {
      const key = value.toLowerCase();
      if (!value || seen.has(key)) return;
      seen.add(key);
      opts.push({ label, value });
    };
    const draft = trackIdInput.trim();
    if (draft) add(draft, draft);
    if (filters.trackId) {
      add(
        `${formatEntityRef("track", filters.trackId)} · ${filters.trackId}`,
        filters.trackId,
      );
    }
    for (const song of songs) {
      add(
        `${formatEntityRef("track", song.trackId)} · ${song.trackId}`,
        song.trackId,
      );
    }
    return opts.slice(0, 40);
  }, [trackIdInput, filters.trackId, songs]);

  const handleTitleSearch = (value: string) => {
    const next = minLenOrUndef(value);
    setTitleInput(next ?? "");
    applyFilters({ qTitle: next });
  };

  const handleArtistSearch = (value: string) => {
    const next = minLenOrUndef(value);
    setArtistInput(next ?? "");
    applyFilters({ qArtist: next });
  };

  const handleTrackIdSearch = (value: string) => {
    const trimmed = value.trim();
    setTrackIdInput(trimmed);
    if (!trimmed) {
      applyFilters({ trackId: undefined });
      return;
    }
    if (isFullTrackId(trimmed)) {
      applyFilters({ trackId: trimmed });
      return;
    }
    // Short TR-… : resolve against the current page set when possible; otherwise
    // keep the typed value visible and clear the server trackId until a full id
    // is pasted (API requires exact track_<uuid>).
    const suffix = normalizeRefSearchTerm("track", trimmed).toUpperCase();
    const match = songs.find((s) => {
      const ref = formatEntityRef("track", s.trackId);
      return (
        ref.replace(/^TR-/, "").startsWith(suffix) ||
        s.trackId.replace(/[^0-9a-z]/gi, "").toUpperCase().endsWith(suffix)
      );
    });
    if (match) {
      setTrackIdInput(match.trackId);
      applyFilters({ trackId: match.trackId });
      return;
    }
    applyFilters({ trackId: undefined });
  };

  const handleFirstUploadedRange = (value: unknown) => {
    const range = value as [dayjs.Dayjs | null, dayjs.Dayjs | null] | null;
    if (!range || (!range[0] && !range[1])) {
      applyFilters({
        firstUploadedFrom: undefined,
        firstUploadedTo: undefined,
      });
      return;
    }
    applyFilters({
      firstUploadedFrom: range[0]
        ? range[0].startOf("day").toISOString()
        : undefined,
      firstUploadedTo: range[1]
        ? range[1].endOf("day").toISOString()
        : undefined,
    });
  };

  const handleLatestActivityRange = (value: unknown) => {
    const range = value as [dayjs.Dayjs | null, dayjs.Dayjs | null] | null;
    if (!range || (!range[0] && !range[1])) {
      applyFilters({
        latestActivityFrom: undefined,
        latestActivityTo: undefined,
      });
      return;
    }
    applyFilters({
      latestActivityFrom: range[0]
        ? range[0].startOf("day").toISOString()
        : undefined,
      latestActivityTo: range[1]
        ? range[1].endOf("day").toISOString()
        : undefined,
    });
  };

  const firstUploadedValue =
    filters.firstUploadedFrom || filters.firstUploadedTo
      ? ([
          filters.firstUploadedFrom ? dayjs(filters.firstUploadedFrom) : null,
          filters.firstUploadedTo ? dayjs(filters.firstUploadedTo) : null,
        ] as [dayjs.Dayjs | null, dayjs.Dayjs | null])
      : null;

  const latestActivityValue =
    filters.latestActivityFrom || filters.latestActivityTo
      ? ([
          filters.latestActivityFrom
            ? dayjs(filters.latestActivityFrom)
            : null,
          filters.latestActivityTo ? dayjs(filters.latestActivityTo) : null,
        ] as [dayjs.Dayjs | null, dayjs.Dayjs | null])
      : null;

  const deletedAtValue =
    filters.deletedFrom || filters.deletedTo
      ? ([
          filters.deletedFrom ? dayjs(filters.deletedFrom) : null,
          filters.deletedTo ? dayjs(filters.deletedTo) : null,
        ] as [dayjs.Dayjs | null, dayjs.Dayjs | null])
      : null;

  const handleDeletedAtRange = (value: unknown) => {
    const range = value as [dayjs.Dayjs | null, dayjs.Dayjs | null] | null;
    if (!range || (!range[0] && !range[1])) {
      applyFilters({
        deletedFrom: undefined,
        deletedTo: undefined,
      });
      return;
    }
    applyFilters({
      deletedFrom: range[0]
        ? range[0].startOf("day").toISOString()
        : undefined,
      deletedTo: range[1] ? range[1].endOf("day").toISOString() : undefined,
    });
  };

  const handleNext = () => {
    const nextToken = currentData?.pageToken;
    if (!nextToken) return;
    setTokenStack((prev) => [...prev, nextToken]);
  };

  const handlePrev = () => {
    setTokenStack((prev) => (prev.length > 1 ? prev.slice(0, -1) : prev));
  };

  const hardError = isError;

  const trackIdFilter = (
    <BootstrapCol md={4}>
      <FormItem
        label="Track ID / TR reference"
        className="customSelectWrapper"
        layout="vertical"
      >
        <Select
          key={`trackId-reset-${resetCounter}`}
          showSearch
          allowClear
          className={`customSelect ${styles.selectField}`}
          placeholder="Any Track ID or TR reference"
          value={filters.trackId ?? null}
          filterOption={false}
          defaultActiveFirstOption={false}
          options={trackIdOptions}
          notFoundContent={
            trackIdInput.trim().length > 0 ? "Press Enter to search" : null
          }
          onSearch={(value) => setTrackIdInput(value)}
          onChange={(value) => {
            if (value == null || value === "") {
              handleTrackIdSearch("");
              return;
            }
            handleTrackIdSearch(String(value));
          }}
          onInputKeyDown={(event) => {
            if (event.key !== "Enter") return;
            event.preventDefault();
            event.stopPropagation();
            handleTrackIdSearch(trackIdInput);
          }}
          aria-label="Filter by Track ID or TR reference"
        />
      </FormItem>
    </BootstrapCol>
  );

  return (
    <Container fluid>
      <Card className={styles.card}>
        <Card.Body>
          <Card.Title className={styles.cardTitle}>
            <PageTitle title="Song History" />
            <Button
              onClick={handleReset}
              style={{
                background: "white",
                color: "#333",
                border: "1px solid #e0e0e0",
                borderRadius: "6px",
                padding: "8px 16px",
                fontWeight: "500",
              }}
            >
              <FilterOutlined /> Reset All
            </Button>
          </Card.Title>

          <p className={styles.helpText}>
            One row per Song. Tabs follow Song Library lifecycle. Open View
            History for the full timeline. Failed uploads that never became a
            Song stay in Song Processing.
          </p>

          <SongHistoryLifecycleTabs
            view={lifecycleStatus}
            onSelect={handleLifecycleSelect}
            activeCountLabel={formatTabCount(tabCountsQuery.data?.active)}
            archivedCountLabel={formatTabCount(tabCountsQuery.data?.archived)}
            deletedCountLabel={formatTabCount(tabCountsQuery.data?.deleted)}
          />

          <Form layout="vertical" className={styles.form}>
            <BootstrapRow className="mb-2">
              <BootstrapCol md={4}>
                <FormItem
                  label="Artist"
                  className="customSelectWrapper"
                  layout="vertical"
                >
                  <Select
                    key={`artist-reset-${resetCounter}`}
                    showSearch
                    allowClear
                    className={`customSelect ${styles.selectField}`}
                    placeholder="Any artist"
                    value={filters.qArtist ?? null}
                    filterOption={false}
                    defaultActiveFirstOption={false}
                    options={artistOptions}
                    notFoundContent={
                      artistInput.trim().length > 0
                        ? "Press Enter to search"
                        : null
                    }
                    onSearch={(value) => setArtistInput(value)}
                    onChange={(value) => {
                      if (value == null || value === "") {
                        handleArtistSearch("");
                        return;
                      }
                      handleArtistSearch(String(value));
                    }}
                    onInputKeyDown={(event) => {
                      if (event.key !== "Enter") return;
                      event.preventDefault();
                      event.stopPropagation();
                      handleArtistSearch(artistInput);
                    }}
                    aria-label="Filter by artist"
                  />
                </FormItem>
              </BootstrapCol>
              <BootstrapCol md={4}>
                <FormItem
                  label="Song title"
                  className="customSelectWrapper"
                  layout="vertical"
                >
                  <Select
                    key={`title-reset-${resetCounter}`}
                    showSearch
                    allowClear
                    className={`customSelect ${styles.selectField}`}
                    placeholder="Any song title"
                    value={filters.qTitle ?? null}
                    filterOption={false}
                    defaultActiveFirstOption={false}
                    options={titleOptions}
                    notFoundContent={
                      titleInput.trim().length > 0
                        ? "Press Enter to search"
                        : null
                    }
                    onSearch={(value) => setTitleInput(value)}
                    onChange={(value) => {
                      if (value == null || value === "") {
                        handleTitleSearch("");
                        return;
                      }
                      handleTitleSearch(String(value));
                    }}
                    onInputKeyDown={(event) => {
                      if (event.key !== "Enter") return;
                      event.preventDefault();
                      event.stopPropagation();
                      handleTitleSearch(titleInput);
                    }}
                    aria-label="Filter by song title"
                  />
                </FormItem>
              </BootstrapCol>
              <BootstrapCol md={4}>
                <FormItem
                  label="Original Uploader"
                  className="customSelectWrapper"
                  layout="vertical"
                >
                  {uploaderDirectory.options.length > 0 ? (
                    <Select
                      key={`uploader-reset-${resetCounter}`}
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      className={`customSelect ${styles.selectField}`}
                      placeholder="Any uploader"
                      value={filters.qUploader}
                      onChange={(value) =>
                        applyFilters({
                          qUploader: value || undefined,
                        })
                      }
                      options={uploaderDirectory.options}
                    />
                  ) : (
                    <Input.Search
                      key={`uploader-reset-${resetCounter}`}
                      allowClear
                      placeholder="Uploader ID"
                      value={uploaderInput}
                      onChange={(e) => setUploaderInput(e.target.value)}
                      onSearch={(value) =>
                        applyFilters({
                          qUploader: minLenOrUndef(value) || undefined,
                        })
                      }
                    />
                  )}
                </FormItem>
              </BootstrapCol>
            </BootstrapRow>

            <BootstrapRow className="mb-2">
              <BootstrapCol md={4}>
                <FormItem label="First Uploaded" layout="vertical">
                  <DateRangePicker
                    key={`firstUploaded-reset-${resetCounter}`}
                    value={firstUploadedValue}
                    onChange={handleFirstUploadedRange}
                  />
                </FormItem>
              </BootstrapCol>
              <BootstrapCol md={4}>
                <FormItem label="Latest Activity" layout="vertical">
                  <DateRangePicker
                    key={`latestActivity-reset-${resetCounter}`}
                    value={latestActivityValue}
                    onChange={handleLatestActivityRange}
                  />
                </FormItem>
              </BootstrapCol>
              {lifecycleStatus === "deleted" ? (
                trackIdFilter
              ) : (
                <BootstrapCol md={4}>
                  <FormItem
                    label="Latest Activity type"
                    className="customSelectWrapper"
                    layout="vertical"
                  >
                    <Select
                      key={`activityType-reset-${resetCounter}`}
                      allowClear
                      className={`customSelect ${styles.selectField}`}
                      placeholder="Any activity"
                      value={filters.latestActivityType}
                      onChange={(value) =>
                        applyFilters({
                          latestActivityType: value || undefined,
                        })
                      }
                      options={[...activityOptions]}
                    />
                  </FormItem>
                </BootstrapCol>
              )}
            </BootstrapRow>

            <BootstrapRow className={`mb-2 ${styles.tabFilterRow}`}>
              {lifecycleStatus === "active" ? (
                <BootstrapCol md={4}>
                  <FormItem
                    label="Current Outcome"
                    className="customSelectWrapper"
                    layout="vertical"
                  >
                    <Select
                      key={`outcome-reset-${resetCounter}`}
                      allowClear
                      className={`customSelect ${styles.selectField}`}
                      placeholder="Any outcome"
                      value={filters.currentOutcome}
                      onChange={(value) =>
                        applyFilters({
                          currentOutcome: value || undefined,
                        })
                      }
                      options={[...outcomeOptions]}
                    />
                  </FormItem>
                </BootstrapCol>
              ) : null}

              {lifecycleStatus === "deleted" ? (
                <>
                  <BootstrapCol md={4}>
                    <FormItem
                      label="Deleted At"
                      className="customSelectWrapper"
                      layout="vertical"
                    >
                      <DateRangePicker
                        key={`deleted-range-reset-${resetCounter}`}
                        value={deletedAtValue}
                        onChange={handleDeletedAtRange}
                      />
                    </FormItem>
                  </BootstrapCol>
                  <BootstrapCol md={4}>
                    <FormItem
                      label="Deleted By"
                      className="customSelectWrapper"
                      layout="vertical"
                    >
                      <Select
                        key={`deleted-by-reset-${resetCounter}`}
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        className={`customSelect ${styles.selectField}`}
                        placeholder="Any deleted by"
                        value={filters.deletedBy ?? null}
                        onChange={(value) => handleDeletedByChange(value)}
                        options={deletedByOptions}
                        aria-label="Filter by deleted by"
                      />
                    </FormItem>
                  </BootstrapCol>
                  <BootstrapCol md={4}>
                    <FormItem
                      label="Deletion Reason"
                      className="customSelectWrapper"
                      layout="vertical"
                    >
                      <Select
                        key={`deletion-reason-reset-${resetCounter}`}
                        showSearch
                        allowClear
                        className={`customSelect ${styles.selectField}`}
                        placeholder="Any deletion reason"
                        value={filters.qDeletionReason ?? null}
                        filterOption={false}
                        defaultActiveFirstOption={false}
                        options={deletionReasonOptions}
                        notFoundContent={
                          deletionReasonInput.trim().length > 0
                            ? "Press Enter to search"
                            : null
                        }
                        onSearch={(value) => setDeletionReasonInput(value)}
                        onChange={(value) => {
                          if (value == null || value === "") {
                            handleDeletionReasonSearch("");
                            return;
                          }
                          handleDeletionReasonSearch(String(value));
                        }}
                        onInputKeyDown={(event) => {
                          if (event.key !== "Enter") return;
                          event.preventDefault();
                          event.stopPropagation();
                          handleDeletionReasonSearch(deletionReasonInput);
                        }}
                        aria-label="Filter by deletion reason"
                      />
                    </FormItem>
                  </BootstrapCol>
                </>
              ) : (
                trackIdFilter
              )}
            </BootstrapRow>
          </Form>

          {hardError ? (
            <Alert variant="danger" className="mb-0">
              {extractTracksErrorMessage(error) ||
                "Failed to load song history. Please refresh and try again."}
            </Alert>
          ) : (
            <Spin spinning={isLoading || isFetching} key={lifecycleStatus}>
              <p className={styles.muted} style={{ marginBottom: 8 }}>
                {matchingTotal != null && currentData && !isFetching
                  ? `Showing ${songs.length.toLocaleString()} of ${matchingTotal.toLocaleString()}${
                      matchingExact ? "" : "+"
                    } matching song${matchingTotal === 1 ? "" : "s"}`
                  : "\u00A0"}
              </p>

              {showDeletedSelection && selectAllMatching && matchingTotal != null ? (
                <Alert
                  variant="info"
                  className="py-2 mb-2 d-flex align-items-center justify-content-between flex-wrap gap-2"
                >
                  <span>
                    All <strong>{matchingTotal.toLocaleString()}</strong> matching
                    Deleted tombstone{matchingTotal === 1 ? "" : "s"}{" "}
                    {matchingTotal === 1 ? "is" : "are"} selected (across all
                    pages).
                  </span>
                  <Button size="sm" variant="outline-dark" onClick={clearSelection}>
                    Clear selection
                  </Button>
                </Alert>
              ) : null}
              {offerSelectAllMatching ? (
                <Alert
                  variant="light"
                  className="py-2 mb-2 d-flex align-items-center justify-content-between flex-wrap gap-2 border"
                >
                  <span>
                    {selectedTrackIds.length.toLocaleString()} tombstone
                    {selectedTrackIds.length === 1 ? "" : "s"} selected on this
                    page.
                  </span>
                  <Button
                    size="sm"
                    variant="link"
                    className="p-0"
                    onClick={() => setSelectAllMatching(true)}
                  >
                    Select all {matchingTotal?.toLocaleString()}
                    {matchingExact ? "" : "+"} matching tombstones
                  </Button>
                </Alert>
              ) : null}

              {showDeletedSelection ? (
                <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                  <FormItem
                    label="Bulk Actions"
                    className="customSelectWrapper mb-0"
                    layout="vertical"
                  >
                    <Select
                      allowClear
                      className={`customSelect ${styles.selectField}`}
                      style={{ minWidth: 220 }}
                      placeholder="Choose an action"
                      value={bulkAction || null}
                      onChange={(value) =>
                        handleBulkActionChange(value ? String(value) : "")
                      }
                      options={[
                        {
                          label: "Tombstone Purge",
                          value: "tombstonePurge",
                        },
                      ]}
                      disabled={
                        purgeBusy ||
                        (!selectAllMatching && selectedTrackIds.length === 0)
                      }
                    />
                  </FormItem>
                </div>
              ) : null}

              <Table
                key={`song-history-table-${lifecycleStatus}`}
                bordered
                hover
                responsive
                className={`legacy-row-height-table ${styles.historyTable}`}
              >
                <thead>
                  <tr>
                    {showDeletedSelection ? (
                      <th style={{ width: 36 }}>
                        <input
                          type="checkbox"
                          aria-label="Select all on this page"
                          checked={allPageSelected}
                          ref={(el) => {
                            if (el) el.indeterminate = somePageSelected;
                          }}
                          onChange={toggleSelectAllPage}
                          disabled={songs.length === 0 || purgeBusy}
                        />
                      </th>
                    ) : null}
                    <th className={styles.colArtist}>Artist</th>
                    <th className={styles.colSong}>Song</th>
                    <th className={styles.colUploader}>Original Uploader</th>
                    <th className={styles.colDate}>First Uploaded</th>
                    <th className={styles.colActivity}>Latest Activity</th>
                    <th className={styles.colOutcome}>Current Outcome</th>
                    <th className={styles.colVersions}>Versions</th>
                    <th className={styles.colActions}>View History</th>
                  </tr>
                </thead>
                <tbody>
                  {songs.length === 0 ? (
                    <tr>
                      <td
                        colSpan={showDeletedSelection ? 9 : 8}
                        className={styles.emptyCell}
                      >
                        {isLoading || isFetching
                          ? " "
                          : lifecycleStatus === "deleted"
                            ? "No deleted Song History tombstones."
                            : lifecycleStatus === "archived"
                              ? "No archived songs in Song History."
                              : "No active songs in Song History."}
                      </td>
                    </tr>
                  ) : (
                    songs.map((row) => {
                      const uploader = uploaderDirectory.resolve(
                        row.originalUploaderSub,
                        row.originalUploaderEmployeeId,
                      );
                      return (
                        <tr key={row.trackId}>
                          {showDeletedSelection ? (
                            <td>
                              <input
                                type="checkbox"
                                aria-label={`Select ${row.title || row.trackId}`}
                                checked={
                                  selectAllMatching ||
                                  selectedTrackIds.includes(row.trackId)
                                }
                                onChange={() => toggleRowSelected(row.trackId)}
                                disabled={purgeBusy}
                              />
                            </td>
                          ) : null}
                          <td>
                            <TruncateCell
                              text={row.artistName}
                              className={styles.truncate}
                            />
                          </td>
                          <td>
                            <TruncateCell
                              text={row.title}
                              className={styles.truncate}
                            />
                          </td>
                          <td>
                            <TruncateCell
                              text={uploader.label}
                              className={
                                uploader.resolved
                                  ? styles.truncate
                                  : `${styles.truncate} ${styles.rawIdFallback}`
                              }
                            />
                          </td>
                          <td className={styles.dateCell}>
                            {formatDateTime(row.firstUploadedAt)}
                          </td>
                          <td>
                            <div className={styles.activityCell}>
                              {renderActivityBadge(
                                row.latestActivityType,
                                row.latestActivityLabel,
                              )}
                              <span className={styles.activityDate}>
                                {formatDateTime(
                                  lifecycleStatus === "deleted"
                                    ? row.permanentlyDeletedAt ||
                                        row.latestActivityAt
                                    : row.latestActivityAt,
                                )}
                              </span>
                              {lifecycleStatus === "deleted" &&
                              row.permanentDeleteReason ? (
                                <span className={styles.activityDate}>
                                  {row.permanentDeleteReason}
                                </span>
                              ) : null}
                            </div>
                          </td>
                          <td>
                            {renderOutcomeBadge(
                              row.currentOutcome,
                              row.currentOutcomeLabel,
                            )}
                          </td>
                          <td className={styles.versionsCell}>
                            {row.versionsDisplay ||
                              (row.playbackVersionNumber != null
                                ? `v${row.playbackVersionNumber}`
                                : "—")}
                          </td>
                          <td>
                            <Button
                              variant="link"
                              size="sm"
                              className={styles.detailLink}
                              onClick={() =>
                                navigate(
                                  `/uploader-history/${encodeURIComponent(row.trackId)}`,
                                )
                              }
                            >
                              View History
                            </Button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </Table>
              <div className="mt-3">
                <PaginationRow
                  page={tokenStack.length}
                  onClickPrevious={handlePrev}
                  handleOnClickNext={handleNext}
                  disableLeft={tokenStack.length === 1 || isFetching}
                  disableNext={!currentData?.pageToken || isFetching}
                />
              </div>
            </Spin>
          )}
        </Card.Body>
      </Card>

      <SongHistoryTombstonePurgeModal
        open={purgeModalOpen}
        requestedCount={Math.max(requestedPurgeCount, 0)}
        selectionMode={selectAllMatching ? "allMatching" : "ids"}
        busy={purgeBusy}
        errorMessage={purgeError}
        onCancel={() => {
          if (purgeBusy) return;
          setPurgeModalOpen(false);
          setPurgeError(null);
          setBulkAction("");
        }}
        onConfirm={(reason) => {
          void submitTombstonePurge(reason);
        }}
      />
    </Container>
  );
};

export default withAuthenticationRequired(UploaderHistory);
